import type {TriggerIntegrationConfigComponentProps} from '@cohort/merchants/apps';
import {useTypeformForm, useTypeformForms} from '@cohort/merchants/apps/typeform/api';
import Button from '@cohort/merchants/components/buttons/Button';
import ErrorState from '@cohort/merchants/components/ErrorState';
import Input from '@cohort/merchants/components/form/v2/Input';
import SelectInput from '@cohort/merchants/components/form/v2/SelectInput';
import type {SelectOption} from '@cohort/merchants/components/form/v2/SelectPicker';
import HighlightText from '@cohort/merchants/components/HighlightText';
import Loader from '@cohort/merchants/components/Loader';
import {useConnections} from '@cohort/merchants/hooks/api/Connections';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import type {StepTriggerFormType} from '@cohort/merchants/pages/campaigns/campaign/edit/settings/journey/utils';
import EditConnectionModal from '@cohort/merchants/pages/connections/new/EditConnectionModal';
import type {MerchantConnectorId} from '@cohort/shared/schema/apps';
import {getAppSpec} from '@cohort/shared/schema/apps';
import type {AppStruct} from '@cohort/shared/schema/apps/app';
import {TypeformTakeQuizTriggerIntegrationSpec} from '@cohort/shared/schema/apps/typeform/triggers/takeQuiz';
import {useEffect, useState} from 'react';
import type {Control, UseFormClearErrors, UseFormRegister, UseFormSetError} from 'react-hook-form';
import {useController} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {isNil} from 'remeda';
import {match} from 'ts-pattern';

type TriggerIntegrationFormSelectProps = {
  register: UseFormRegister<StepTriggerFormType>;
  control: Control<StepTriggerFormType>;
  connectionId: string;
  setError: UseFormSetError<StepTriggerFormType>;
  clearErrors: UseFormClearErrors<StepTriggerFormType>;
};

const TriggerIntegrationFormSelect: React.FC<TriggerIntegrationFormSelectProps> = ({
  register,
  control,
  connectionId,
  setError,
  clearErrors,
}) => {
  const merchant = useCurrentMerchant();
  const {field: formIdField} = useController({name: 'triggerIntegrationConfig.formId', control});
  const {field: triggerIntegrationId} = useController({name: 'triggerIntegrationId', control});
  const {t} = useTranslation('app-typeform', {
    keyPrefix: 'triggerIntegrations.configComponent',
  });

  const {
    data: typeformFormsRes,
    isLoading: isTypeformFormsLoading,
    isFetched: isTypeformFormsFetched,
  } = useTypeformForms(merchant.id, connectionId);

  const {data: typeformFormRes, isFetched: isTypeformFormFetched} = useTypeformForm(
    merchant.id,
    connectionId,
    formIdField.value as string,
    {
      enabled:
        // @Devs, fetch the form only when it's a quiz.
        triggerIntegrationId.value === TypeformTakeQuizTriggerIntegrationSpec.id &&
        formIdField.value !== undefined,
    }
  );

  const typeformForms = typeformFormsRes?.output.forms ?? [];
  const sortedTypeformForms = typeformForms.sort(formA => (formA.settings.is_public ? -1 : 1));

  useEffect(() => {
    if (isTypeformFormFetched) {
      // @Devs, when the form is a quiz, check if the form has a score variable.
      if (
        triggerIntegrationId.value === TypeformTakeQuizTriggerIntegrationSpec.id &&
        typeformFormRes?.output.form.variables?.score === undefined
      ) {
        setError('triggerIntegrationConfig.formId', {
          type: 'manual',
          message: t('errorTypeformQuizWithFillFormIntegration'),
        });
      } else {
        clearErrors('triggerIntegrationConfig.formId');
      }
    }
  }, [
    triggerIntegrationId.value,
    isTypeformFormFetched,
    typeformFormRes?.output.form.variables?.score,
    setError,
    clearErrors,
    t,
  ]);

  const formsSelectionContent = match({
    typeformForms,
    isTypeformFormsLoading,
    isTypeformFormsFetched,
    formIdFieldValue: formIdField.value,
  })
    .with({isTypeformFormsLoading: true}, () => <Loader size={30} color="gray" />)
    .with({typeformForms: []}, () => (
      <div className="flex flex-col space-y-2">
        <p>{t('formSelect.noFormsMessage')}</p>
      </div>
    ))
    .with({isTypeformFormsFetched: true}, () => (
      <div>
        <SelectInput
          labelPosition="top"
          label={t('formSelect.labelFormSelect')}
          description={t('formSelect.descriptionFormSelect')}
          displayMode="expand"
          options={sortedTypeformForms.map(
            form =>
              ({
                value: form.id,
                label: form.settings.is_public
                  ? form.title
                  : t('formSelect.privateFormLabel', {title: form.title}),
                isDisabled: !form.settings.is_public,
              }) satisfies SelectOption
          )}
          name="triggerIntegrationConfig.formId"
          register={register}
          control={control}
        />
        <div className="mt-2">
          <HighlightText
            type="info"
            text={<label>{t('formSelect.formSelectInfoMessage')}</label>}
          />
        </div>

        {triggerIntegrationId.value === TypeformTakeQuizTriggerIntegrationSpec.id && (
          <div className="mt-4 w-1/4 min-w-8">
            <Input
              label={t('formSelect.labelMinScore')}
              labelPosition="top"
              type="number"
              name="triggerIntegrationConfig.minScore"
              min={0}
              placeholder="0"
              required
              register={register}
              control={control}
            />
          </div>
        )}
      </div>
    ))
    .otherwise(() => <ErrorState />);

  return formsSelectionContent;
};

type TriggerIntegrationConnectionPickerProps<T extends AppStruct> = {
  appId: T['Id'];
  register: UseFormRegister<StepTriggerFormType>;
  control: Control<StepTriggerFormType>;
  onConnectionModalClose?: () => void;
};

const TriggerIntegrationConnectionPicker = <T extends AppStruct>({
  appId,
  register,
  control,
  onConnectionModalClose,
}: TriggerIntegrationConnectionPickerProps<T>): React.ReactElement => {
  const merchant = useCurrentMerchant();
  const [createConnectionModalOpen, setCreateConnectionModalOpen] = useState(false);
  const {t} = useTranslation('app-typeform', {
    keyPrefix: 'triggerIntegrations.configComponent',
  });

  const appSpec = getAppSpec(appId);

  if (appSpec.merchantConnector === null) {
    throw new Error(`App ${appId} does not support merchant connections`);
  }

  const {data: connections} = useConnections(merchant.id, {
    connectorId: [appSpec.merchantConnector.id as MerchantConnectorId],
  });
  const readyConnections = connections?.filter(connection => connection.status === 'ready');

  const {field: connectionIdField} = useController({name: 'connectionId', control});

  useEffect(() => {
    if (
      isNil(connectionIdField.value) &&
      readyConnections !== undefined &&
      readyConnections.length > 0
    ) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      connectionIdField.onChange(readyConnections[0]!.id);
    }
  }, [connectionIdField, readyConnections]);

  if (!readyConnections) {
    return (
      <div className="space-y-6">
        <Loader size={30} color="gray" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {readyConnections.length > 0 && (
        <SelectInput
          name="connectionId"
          label={t('connectionPicker.labelConnection')}
          register={register}
          control={control}
          options={readyConnections.map(
            connection =>
              ({
                label: connection.name,
                value: connection.id,
                isDisabled: connection.status !== 'ready',
              }) satisfies SelectOption
          )}
        />
      )}
      <Button
        data-testid="create-connection-btn"
        onClick={() => {
          setCreateConnectionModalOpen(true);
        }}
      >
        {t('connectionPicker.buttonCreateConnection')}
      </Button>
      {createConnectionModalOpen && (
        <EditConnectionModal
          onClose={() => {
            setCreateConnectionModalOpen(false);
            onConnectionModalClose?.();
          }}
          appId={appId}
          existingConnection={undefined}
        />
      )}
    </div>
  );
};

// @Devs - Using the same config component for Fill form and Quiz trigger integrations.
const TypeformTriggerIntegrationConfigComponent: React.FC<
  TriggerIntegrationConfigComponentProps
> = ({control, register, setError, clearErrors}) => {
  const merchant = useCurrentMerchant();
  const {field: connectionIdField} = useController({
    control,
    name: 'connectionId',
  });
  const [showFormSelect, setShowFormSelect] = useState(false);
  const [connectionIdValue, setConnectionIdValue] = useState<string | null>(
    connectionIdField.value
  );

  const {data: connections} = useConnections(merchant.id, {connectorId: ['typeform']});

  useEffect(() => {
    if (connections === undefined) {
      return;
    }
    const readyConnections = connections.filter(connection => connection.status === 'ready');
    if (readyConnections.length > 0) {
      setConnectionIdValue(readyConnections[0]?.id ?? null);
      setShowFormSelect(true);
    }
  }, [connections, connectionIdField.value]);

  const handleConnectionModalClose = (): void => {
    // @Devs, show the Forms select only when the modal is closed and the connection is ready.
    setShowFormSelect(true);
  };

  const connectionId = connectionIdField.value ?? connectionIdValue;

  if (setError === undefined || clearErrors === undefined) {
    throw new Error('setError() & clearErrors() are required.');
  }

  return (
    <div className="space-y-4">
      <TriggerIntegrationConnectionPicker
        appId="typeform"
        register={register}
        control={control}
        onConnectionModalClose={handleConnectionModalClose}
      />
      {showFormSelect && connectionId !== null && (
        <TriggerIntegrationFormSelect
          register={register}
          control={control}
          connectionId={connectionId}
          setError={setError}
          clearErrors={clearErrors}
        />
      )}
    </div>
  );
};

export default TypeformTriggerIntegrationConfigComponent;
